import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TitleBadge } from './TitleBadge';
import type { TitleType } from './TitleBadge';
import type { UserTitle, AvatarFrame as AvatarFrameType } from '../../services/progressionService';
import './JourneyPath.css';

interface JourneyNode {
    level: number;
    title: UserTitle;
    frame: AvatarFrameType;
    isCompleted: boolean;
    isCurrent: boolean;
    isNext: boolean;
}

// Colors for each milestone tier
const NODE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    none: { bg: '#2D6A4F', border: '#1B4332', text: '#fff' },
    bronze: { bg: '#8B5E3C', border: '#5C3D2E', text: '#fff' },
    silver: { bg: '#718096', border: '#4A5568', text: '#fff' },
    gold: { bg: '#B8860B', border: '#7B5B1E', text: '#fff' },
    platinum: { bg: '#4A5568', border: '#2D3748', text: '#fff' },
    diamond: { bg: '#2B6CB0', border: '#1A3A4A', text: '#fff' },
};

interface JourneyPathProps {
    currentLevel: number;
    unlockedTitles: UserTitle[];
    unlockedFrames: AvatarFrameType[];
    avatarUrl?: string;
}

const JOURNEY_NODES: { level: number; title: UserTitle; frame: AvatarFrameType }[] = [
    { level: 1, title: 'beginner', frame: 'none' },
    { level: 5, title: 'apprentice', frame: 'bronze' },
    { level: 10, title: 'scholar', frame: 'silver' },
    { level: 15, title: 'expert', frame: 'gold' },
    { level: 20, title: 'master', frame: 'platinum' },
    { level: 25, title: 'legend', frame: 'diamond' },
];

export const JourneyPath: React.FC<JourneyPathProps> = ({
    currentLevel,
    unlockedTitles,
    unlockedFrames,
    avatarUrl,
}) => {
    const { t } = useTranslation('progression');
    const scrollRef = useRef<HTMLDivElement>(null);
    const currentNodeRef = useRef<HTMLDivElement>(null);

    const nodes: JourneyNode[] = JOURNEY_NODES.map((node, index) => {
        const nextNode = JOURNEY_NODES[index + 1];
        const isCurrent = currentLevel >= node.level && (nextNode ? currentLevel < nextNode.level : currentLevel >= node.level);
        return {
            ...node,
            isCompleted: currentLevel >= node.level && !isCurrent,
            isCurrent,
            isNext: !isCurrent && currentLevel < node.level && (index === 0 || currentLevel >= JOURNEY_NODES[index - 1].level),
        };
    });

    // Auto-scroll to center current node
    useEffect(() => {
        if (currentNodeRef.current && scrollRef.current) {
            const container = scrollRef.current;
            const node = currentNodeRef.current;
            const scrollLeft = node.offsetLeft - container.clientWidth / 2 + node.clientWidth / 2;
            container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
        }
    }, [currentLevel]);

    // Calculate progress between current milestone and next
    const getCurrentSegmentProgress = (nodeIndex: number): number => {
        const node = JOURNEY_NODES[nodeIndex];
        const nextNode = JOURNEY_NODES[nodeIndex + 1];
        if (!nextNode) return currentLevel >= node.level ? 100 : 0;
        if (currentLevel >= nextNode.level) return 100;
        if (currentLevel < node.level) return 0;
        return ((currentLevel - node.level) / (nextNode.level - node.level)) * 100;
    };

    return (
        <div className="journey-path-wrapper">
            <h2 className="journey-title">{t('journey.title')}</h2>
            <div className="journey-scroll" ref={scrollRef}>
                <div className="journey-track">
                    {nodes.map((node, index) => (
                        <React.Fragment key={node.level}>
                            {/* Node */}
                            <div
                                className={`journey-node ${node.isCompleted ? 'completed' : ''} ${node.isCurrent ? 'current' : ''} ${node.isNext ? 'next' : ''} ${!node.isCompleted && !node.isCurrent ? 'locked' : ''}`}
                                ref={node.isCurrent ? currentNodeRef : undefined}
                            >
                                <div className="journey-node-visual">
                                    {node.isCurrent ? (
                                        <div
                                            className="journey-node-current-circle"
                                            style={{
                                                background: NODE_COLORS[node.frame].bg,
                                                borderColor: NODE_COLORS[node.frame].border,
                                                color: NODE_COLORS[node.frame].text,
                                            }}
                                        >
                                            <span className="journey-node-current-level">{node.level}</span>
                                        </div>
                                    ) : node.isCompleted ? (
                                        <div
                                            className="journey-node-completed-circle"
                                            style={{
                                                background: NODE_COLORS[node.frame].bg,
                                                borderColor: NODE_COLORS[node.frame].border,
                                            }}
                                        >
                                            <span className="journey-node-check-icon">&#10003;</span>
                                        </div>
                                    ) : (
                                        <div className={`journey-node-locked-circle ${node.isNext ? 'next-glow' : ''}`}>
                                            <span className="journey-node-lock">?</span>
                                        </div>
                                    )}
                                </div>
                                <div className="journey-node-label">
                                    <span className="journey-node-level">Lv.{node.level}</span>
                                    <TitleBadge title={node.title as TitleType} size={24} />
                                    <span className="journey-node-title">{t(`titles.${node.title}`)}</span>
                                    {node.frame !== 'none' && (
                                        <span className="journey-node-frame">{t(`frames.${node.frame}`)}</span>
                                    )}
                                </div>
                                {node.isCurrent && (
                                    <div className="journey-you-badge">{t('journey.youAreHere')}</div>
                                )}
                            </div>

                            {/* Connector */}
                            {index < nodes.length - 1 && (
                                <div className="journey-connector">
                                    <div className="journey-connector-bg" />
                                    <div
                                        className="journey-connector-fill"
                                        style={{ width: `${getCurrentSegmentProgress(index)}%` }}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default JourneyPath;
